import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authAPI } from '../api';
import { Icon } from '../components/ui';
import './Auth.css';

/**
 * Where a password-reset link lands.
 *
 * The link is a live credential for an account that already exists, so it is deliberately
 * short-lived and single-use: the token carries a fingerprint of the password it was minted
 * against, and setting a new one invalidates every outstanding link — including this one.
 *
 * The failure messages here do not distinguish expired from tampered from already-spent,
 * because the server does not either. Telling them apart would say something about the
 * account to whoever is holding the link, and the person who legitimately needs it takes
 * the same action in all three cases: ask for a new one.
 */
/** The Rooman mark + wordmark, so the reset-password card is branded like the rest of auth. */
function AuthBrand() {
  return (
    <Link to="/" className="auth-card-brand" aria-label="Rooman Projects home">
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden>
        <rect width="12" height="12" rx="3" fill="#444ce7" />
        <rect x="16" width="12" height="12" rx="3" fill="#f25c2b" />
        <rect y="16" width="12" height="12" rx="3" fill="#3b6df8" />
        <rect x="16" y="16" width="12" height="12" rx="3" fill="#a855f7" />
      </svg>
      <span>Rooman Projects</span>
    </Link>
  );
}

export default function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(
    () => new URLSearchParams(location.search).get('token') || '',
    [location.search],
  );

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    // 10, matching AUTH_PASSWORD_VALIDATORS. Advertising a lower number would reject a
    // compliant-looking password at the last step, which is the worst moment to learn a
    // rule — the same defect the invite flow had.
    if (password.length < 10) next.password = 'Password must be at least 10 characters.';
    if (password !== confirm) next.confirm = 'The two passwords do not match.';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      await authAPI.confirmPasswordReset(token, password, confirm);
      setDone(true);
    } catch (err: any) {
      const data = err?.response?.data || {};
      setErrors({
        general: data.error || '',
        password: Array.isArray(data.password) ? data.password.join(' ') : data.password || '',
        confirm: data.confirm_password || '',
        ...(data.detail ? { general: data.detail } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <AuthBrand />
          <h1 className="auth-title">Reset your password</h1>
          <div className="auth-error" role="alert">
            <span className="auth-error-icon"><Icon name="warning" size={14} /></span>
            This page needs a reset link. Choose “Forgot password?” on the sign-in page to
            get one.
          </div>
          <Link to="/login" className="btn-submit btn-submit-flex"
                style={{ textDecoration: 'none', justifyContent: 'center' }}>
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <AuthBrand />
          <h1 className="auth-title">Password changed</h1>
          <div className="auth-notice" role="status">
            <span className="auth-error-icon"><Icon name="checkCircle" size={14} /></span>
            You can sign in with your new password now. This link will not work again.
          </div>
          <button type="button" className="btn-submit btn-submit-flex"
                  onClick={() => navigate('/login', { replace: true })}>
            Continue to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
          <AuthBrand />
        <h1 className="auth-title">Choose a new password</h1>
        <p className="auth-form-sub" style={{ marginBottom: 16 }}>
          This link is valid for one hour and works once.
        </p>

        {errors.general && (
          <div className="auth-error" role="alert">
            <span className="auth-error-icon"><Icon name="warning" size={14} /></span>
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label" htmlFor="reset-password">New password</label>
            <input
              id="reset-password"
              name="password"
              type="password"
              className="form-input"
              placeholder="Minimum 10 characters"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={Boolean(errors.password)}
              aria-describedby={errors.password ? 'reset-password-error' : undefined}
            />
            {errors.password && (
              <div className="form-error-msg" id="reset-password-error">{errors.password}</div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="reset-confirm">Confirm new password</label>
            <input
              id="reset-confirm"
              name="confirm_password"
              type="password"
              className="form-input"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={Boolean(errors.confirm)}
              aria-describedby={errors.confirm ? 'reset-confirm-error' : undefined}
            />
            {errors.confirm && (
              <div className="form-error-msg" id="reset-confirm-error">{errors.confirm}</div>
            )}
          </div>

          <button type="submit" className="btn-submit btn-submit-flex" disabled={saving}>
            {saving ? 'Saving…' : 'Set new password'}
          </button>
        </form>

        <div className="auth-alt" style={{ marginTop: 16 }}>
          <Link to="/login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
